import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@app/redux/store';
import { RootState } from '@app/redux/types';

import useDatadog from '@app/hooks/useDatadog';

import { WorkspaceIndicatorToast } from '@app/components/modules/workspace/WorkspaceIndicatorToast';

import TokenStorage from '@app/services/Auth/TokenStorage';
import { checkShouldSetUser } from './utils';

import { PageWrapper } from './PrivateRoute.styles';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { PageRouteEnum } from '@app/constants/pages';
import { WorkspacePlansEnum } from '@app/constants/modules/workspace';
import { getWorkspacePlans } from '@app/utils/modules/workspace';
import { checkIsWorkspaceAdmin } from '@app/utils/modules/storytellers';

import * as S from './PrivateRoute.styles';
import { useAuth } from '@app/services/Auth/useAuth';
import authConfig from '@app/services/Auth/authConfig';
import LocalStorage from '@app/utils/LocalStorage';
import { SpinningLoader } from '@nicheinc/campus-ds';
import checkIsGKAdmin from '@app/utils/checkIsGKAdmin';

interface PrivateRouteProps {
	children: ReactNode;
	adminRoute?: boolean;
	nicheAdminRoute?: boolean;
	redirectTo?: string;
	workspacePlans?: WorkspacePlansEnum[];
	pageWrapperOverflowHidden?: boolean;
	setIsShowingNavBar: (isShowingNavBar: boolean) => void;
	noContainer?: boolean;
	disabledRoute?: boolean;
}

export default function PrivateRoute({
	children,
	adminRoute,
	nicheAdminRoute,
	redirectTo,
	workspacePlans,
	pageWrapperOverflowHidden,
	setIsShowingNavBar,
	noContainer = false,
	disabledRoute = false,
}: PrivateRouteProps) {
	const dispatch = useAppDispatch();

	const { user: loggedInUser, isLoading, authProvider } = useAuth();

	const navigate = useNavigate();

	const location = useLocation();

	const { setDatadogUser } = useDatadog();

	const growthbook = useGrowthBook();

	const { workspaceInfo } = useSelector(({ auth, workspace }: RootState) => ({
		workspaceInfo: workspace?.workspaceInfo,
	}));

	const workspaceLoading = useSelector(
		({ workspace }: RootState) => workspace.loading,
	);

	const allWorkspacePlans = getWorkspacePlans(workspaceInfo);

	const hasPlansToMatchOnTheRoute =
		workspacePlans && !R.isEmpty(workspacePlans);

	const hasAnyOfTheRoutePlans = useMemo(() => {
		if (!hasPlansToMatchOnTheRoute) {
			return true;
		}

		return allWorkspacePlans.some((plan) => {
			return workspacePlans?.includes(plan);
		});
	}, [hasPlansToMatchOnTheRoute, workspacePlans, allWorkspacePlans]);

	const isAdmin = checkIsWorkspaceAdmin(loggedInUser);

	const isNicheAdmin = checkIsGKAdmin(loggedInUser?.email);

	if (!isLoading && !authProvider?.isAuthenticated) {
		navigate('/', { state: { fromOther: true } });
	}

	//Redirect user based on plans
	useEffect(() => {
		if (
			hasPlansToMatchOnTheRoute &&
			!workspaceLoading &&
			workspaceInfo._id &&
			!hasAnyOfTheRoutePlans
		) {
			navigate(redirectTo || '/');
		}
	}, [
		hasPlansToMatchOnTheRoute,
		hasAnyOfTheRoutePlans,
		workspaceLoading,
		workspaceInfo,
		redirectTo,
		navigate,
	]);

	// Redirects unauthorized users
	useEffect(() => {
		if (
			authProvider?.isAuthenticated &&
			TokenStorage.getIdentity() &&
			loggedInUser
		) {
			if (adminRoute && !isAdmin) {
				navigate(redirectTo || '/');
			}

			if (nicheAdminRoute && !isNicheAdmin) {
				navigate(redirectTo || '/');
			}
		}
	}, [
		authProvider?.isAuthenticated,
		adminRoute,
		isAdmin,
		loggedInUser,
		nicheAdminRoute,
		isNicheAdmin,
	]);

	// Callers derive `disabledRoute` from the logged-in user and workspace, both
	// of which load asynchronously. Until they resolve, those flags sit at their
	// least-privileged value, so redirecting on them would bounce users off
	// routes they can actually access.
	const isRouteGatingReady =
		!isLoading &&
		!!loggedInUser?.id &&
		!workspaceLoading &&
		!!workspaceInfo?._id;

	//Redirect if the route is disabled
	useEffect(() => {
		if (isRouteGatingReady && disabledRoute) {
			navigate(redirectTo || '/');
		}
	}, [isRouteGatingReady, disabledRoute]);

	// create ref to avoid rerendering calls & identify user on login
	const loginRef = useRef(false);

	// Identifies user to Datadog
	useEffect(() => {
		if (!loggedInUser?.id || !workspaceInfo?._id) return;

		// Saves workspace and user ids on storage to avoid rerendering calls
		const dataDogWorkspaceId =
			sessionStorage.getItem('dataDogWorkspaceId') || workspaceInfo?._id;
		const dataDogStorytellerId =
			sessionStorage.getItem('dataDogStorytellerId') || loggedInUser?.id;

		// Allows to set datadog user on workspace/user change
		const shouldIdentifyUser = checkShouldSetUser(
			dataDogWorkspaceId,
			dataDogStorytellerId,
			workspaceInfo._id,
			loggedInUser.id,
		);
		if (shouldIdentifyUser || !loginRef.current) {
			const workspaceId = workspaceInfo._id || workspaceInfo.id;
			const storytellerId = loggedInUser.id || loggedInUser._id;

			setDatadogUser();
			sessionStorage.setItem('dataDogWorkspaceId', workspaceId);
			sessionStorage.setItem('dataDogStorytellerId', storytellerId);

			growthbook?.setAttributes({
				workspaceId,
				storytellerId,
			});

			if (!loginRef.current) loginRef.current = true;
		}
	}, [workspaceInfo?._id, loggedInUser?.id]);

	// Pathname Listener for Navbar
	useEffect(() => {
		// Modifications while in Campaign builder
		const isCampaignBuilderRoute = location.pathname.includes(
			PageRouteEnum.campaignCreation,
		);

		const isAuthRoute =
			location.pathname.includes(PageRouteEnum.callback) ||
			location.pathname.includes(PageRouteEnum.changingWorkspace) ||
			location.pathname.includes(PageRouteEnum.changingWorkspaceFallback);

		const isRootRoute = location.pathname === '/';

		if (setIsShowingNavBar) {
			if (
				isCampaignBuilderRoute ||
				isAuthRoute ||
				isRootRoute ||
				!authProvider?.isAuthenticated
			) {
				setIsShowingNavBar(false);
			} else {
				setIsShowingNavBar(true);
			}
		}
	}, [location.pathname, authProvider?.isAuthenticated]);

	const hasIdentityToken = TokenStorage.getIdentity();

	const isLoggedIn = useMemo(() => {
		return authProvider?.isAuthenticated && hasIdentityToken;
	}, [authProvider?.isAuthenticated, hasIdentityToken]);

	useEffect(() => {
		if (!authProvider?.isLoading && !isLoggedIn) {
			LocalStorage.clear();
			authProvider?.logout({ returnTo: authConfig.logoutUrl } as any);
			navigate('/');
		}
	}, [authProvider?.isLoading, isLoggedIn]);

	useEffect(() => {
		const handleReloadOnWorkspaceChange = () => {
			//if the workspace has changed to the localStorage workspaceId then reload the page
			const workspaceId = localStorage.getItem('lastWorkspaceId');
			if (workspaceId !== workspaceInfo?.id) {
				window.location.reload();
			}
		};

		document.addEventListener(
			'visibilitychange',
			handleReloadOnWorkspaceChange,
		);

		return () => {
			document.removeEventListener(
				'visibilitychange',
				handleReloadOnWorkspaceChange,
			);
		};
	}, [workspaceInfo?.id]);

	// Show a loader when this HoC is still making the necessary checks based on the user's data
	if (!loggedInUser?.id || isLoading) {
		return (
			<S.LoaderContainer>
				<SpinningLoader />
			</S.LoaderContainer>
		);
	}

	if (noContainer) {
		return <>{children}</>;
	}

	return (
		<PageWrapper
			id="page-wrapper"
			pageWrapperOverflowHidden={pageWrapperOverflowHidden}
		>
			<WorkspaceIndicatorToast
				userEmail={loggedInUser?.email}
				organizationName={workspaceInfo?.organizationName}
			/>
			{children}
		</PageWrapper>
	);
}
